import React, { FC, useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { FieldGroup } from '../FieldGroup';
import { FormTitle } from '../FormTitle';
import { ToggleCheckbox } from '../../toggle-checkbox';
import { formatClassName, pickDataAttributes } from '../../../utils';
import { noop } from '../../../utils/noop';
import { Card as CoreCard, CardBody as CoreCardBody, Layout as CoreLayout } from '../../../core';
import { tokens } from '@everfi/design-system';

interface FormSectionProps {
  children?: object;
  className?: string;
  data?: object;
  description?: string;
  isCard?: boolean;
  onToggle?: (value: boolean) => void;
  title?: string;
  toggle?: {
    checked?: boolean;
    label?: string;
    name: string;
    defaultChecked?: boolean;
    style?: 'absolute' | 'relative' | 'fixed' | 'secondary';
  } | null;
}

/** renders a form section */
export const FormSection: FC<FormSectionProps> = ({
  children,
  className = '',
  data = null,
  description = '',
  isCard = false,
  onToggle = noop,
  title,
  toggle = null,
  ...props
}) => {
  const [toggled, setToggled] = useState(() => {
    return !toggle || (data && data[toggle.name]) || toggle.checked;
  });

  const handleToggle = value => {
    setToggled(value);
    onToggle(value);
  };

  const sectionClassName = formatClassName(['form-section', className, { 'form-section-card': isCard }]);

  const inner = (
    <FieldGroup {...props} data={data} className={sectionClassName}>
      {toggle && (
        <ToggleCheckbox
          {...pickDataAttributes(toggle)}
          className={`round sm ${toggle.style ?? ''}`}
          checked={toggled}
          label={toggle.label}
          name={toggle.name}
          onChange={event => {
            handleToggle(event.target.checked);
          }}
        />
      )}
      {title && <FormTitle className={description ? 'with-description' : ''}>{title}</FormTitle>}
      {description && <p className="form-section-description p2">{description}</p>}
      <Collapse in={toggled}>
        <FieldGroup {...props} data={data}>
          {children}
        </FieldGroup>
      </Collapse>
    </FieldGroup>
  );
  if (isCard) {
    return (
      <CoreLayout marginBottom={tokens.SpacingBaseMd}>
        <CoreCard variant="outline" sx={{ overflow: 'initial' }}>
          <CoreLayout paddingTop={tokens.SpacingBaseMd}>
            <CoreCardBody>{inner}</CoreCardBody>
          </CoreLayout>
        </CoreCard>
      </CoreLayout>
    );
  } else {
    return inner;
  }
};
